import type { CalendarEvent } from 'src/meetings/models';

type ClientGuest = {
  name: string;
  participantType: 'CLIENT';
  clientId: string;
};

type UserGuest = {
  name: string;
  participantType: 'USER';
  userId: string;
};

type GuestDisplayGuest = ClientGuest | UserGuest;

type GuestDisplayData = {
  totalGuests: number;
  knownGuests: GuestDisplayGuest[];
  unknownCount: number;
};

const buildFullName = (firstName?: string | null, lastName?: string | null): string | undefined => {
  const parts = [firstName, lastName].filter(
    (part): part is string => typeof part === 'string' && part.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(' ') : undefined;
};

export const getGuestDisplayData = (event: CalendarEvent): GuestDisplayData => {
  const knownGuests = event.eventParticipants.flatMap<GuestDisplayGuest>(participant => {
    const { client, user } = participant.participant;

    if (client) {
      const name = buildFullName(client.firstName, client.lastName);
      return name
        ? [
            {
              name,
              participantType: 'CLIENT',
              clientId: client.id,
            },
          ]
        : [];
    }

    if (user) {
      const name = buildFullName(user.firstName, user.lastName);
      return name
        ? [
            {
              name,
              participantType: 'USER',
              userId: user.id,
            },
          ]
        : [];
    }

    return [];
  });

  const totalGuests = event.eventParticipants.length;
  const unknownCount = Math.max(totalGuests - knownGuests.length, 0);

  return {
    totalGuests,
    knownGuests,
    unknownCount,
  };
};

export const getFirstClientParticipant = (
  event: CalendarEvent,
): CalendarEvent['eventParticipants'][0] | undefined => {
  return event.eventParticipants.find(p => p.participant.client !== null);
};

export const getAllClientParticipants = (
  event: CalendarEvent,
): CalendarEvent['eventParticipants'] => {
  return event.eventParticipants.filter(p => p.participant.client !== null);
};

export const getParticipantFullName = (
  participant: CalendarEvent['eventParticipants'][0] | undefined,
): string | undefined => {
  if (!participant) {
    return undefined;
  }

  const { client, user } = participant.participant;

  if (client) {
    return buildFullName(client.firstName, client.lastName);
  }

  if (user) {
    return buildFullName(user.firstName, user.lastName);
  }

  return undefined;
};
